import classNames from 'classnames';
import React, { useState } from 'react';
import { Menu, MenuItemProps, Popup, PopupProps, StrictMenuItemProps } from 'semantic-ui-react';
import './GfActionMenu.scss';
import GfActionMenuIcon from './GfActionMenuIcon';

/**
 * Represents the props for a menu item in the ActionMenu.
 * Extends the StrictMenuItemProps from semantic-ui-react.
 */
export type ActionMenuItemProps = StrictMenuItemProps & {
  key?: string;
  visible?: boolean;
  tooltipText?: string;
  /** A custom icon component to use. This component takes priority over the `icon` from the `Popup`. */
  iconComponent?: React.ReactElement;
};

/**
 * Represents the props for the ActionMenu component.
 * Extends the React.HTMLAttributes<HTMLDivElement> and PopupProps from semantic-ui-react.
 */
export type ActionMenuProps = {
  buttonClassName?: string;
  items: ActionMenuItemProps[];
  menuClassName?: string;
  onActionMenuClose?: VoidFunc;
  onActionMenuOpen?: VoidFunc;
  onClick?: VoidFunc;
  vertical?: boolean;
} & React.HTMLAttributes<HTMLDivElement> &
  PopupProps;

/**
 * ActionMenu component renders a clickable trigger button that opens a popup menu with a list of menu items.
 * @param {ActionMenuProps} props - The props for the ActionMenu component.
 * @returns {JSX.Element} The rendered ActionMenu component.
 */
export default function GfActionMenu(props: ActionMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  // tooltipOpen is the name of the item that has a tooltip open
  const [tooltipItem, setTooltipItem] = useState<string>(null);

  const handleTriggerClick = () => {
    setIsOpen(true);
    props.onActionMenuOpen?.();
    props.onClick?.();
  };

  const handleMenuItemClick = (item: ActionMenuItemProps, e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, data: MenuItemProps) => {
    e.stopPropagation(); // Stop event propagation to prevent closing the parent menu if exists
    setIsOpen(false);
    item.onClick(e, data);
    props.onActionMenuClose?.();
  };

  const handleTooltipOpen = (item: ActionMenuItemProps) => {
    setTooltipItem(!!item.disabled && !!item.tooltipText ? item.name : null);
  };

  const handleTooltipClose = () => {
    setTooltipItem(null);
  };

  const actionMenuContent = (
    <Menu role="menu" vertical className={classNames('action-menu-list', props.menuClassName || '')}>
      {(props.items || [])
        .filter((item) => item.visible !== false)
        .map((item, index) => (
          // wrap Menu.Item in a popup so we can show a tooltip for disabled items
          <Popup
            className="bulk-action-menu-item-popup"
            content={item.tooltipText}
            position="right center"
            on="hover"
            key={item.key || `action-menu-item-${index}`}
            trigger={
              // vh - why can disabled action items clickable?
              // disabled Menu.Item does not trigger popup, so we need to wrap it in a span
              <span>
                <Menu.Item
                  {...{
                    ...item,
                    key: item.key || `action-menu-item-${index}`,
                    visible: undefined,
                    icon: undefined,
                    iconComponent: undefined,
                    role: 'menuitem',
                    position: 'right',
                    onClick: (e, data) => handleMenuItemClick(item, e, data)
                  }}
                >
                  {(item.icon || item.iconComponent) && <GfActionMenuIcon icon={item.icon}>{item.iconComponent}</GfActionMenuIcon>}
                  {item.name}
                </Menu.Item>
              </span>
            }
            open={tooltipItem === item.name}
            onOpen={() => handleTooltipOpen(item)}
            onClose={handleTooltipClose}
          />
        ))}
    </Menu>
  );

  return (
    <Popup
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
        props.onActionMenuClose?.();
      }}
      on="click"
      className={classNames('action-menu', props.className || '')}
      trigger={
        props.trigger || (
          <button
            type="button"
            className={classNames('action-menu-button', props.buttonClassName || '')}
            onClick={handleTriggerClick}
            disabled={props.disabled}
          >
            <i className={classNames('ellipsis icon', { horizontal: !props.vertical, vertical: props.vertical })} />
          </button>
        )
      }
      position={props.position || 'top right'}
      content={actionMenuContent}
    ></Popup>
  );
}
